import LivingMap, {
  LivingMapPlugin,
  LMFeature,
  GlobalFilters,
} from "@livingmap/core-mapping";
import MapGeometryHighlighter from "./geometry-visualisation/geometry-highlight";
import MapGeometryHover from "./geometry-visualisation/geometry-hover";
import MapGeometrySelector, {
  SelectFeatureOptions,
} from "./geometry-visualisation/geometry-select";
import UserClickAction from "./user-interactions/user-click-actioner";
import UserMouseMoveAction from "./user-interactions/user-mousemove-actioner";
import { SourceIds, FeatureLayers } from "./types/index";
import RoutingPlugin from "./routing-control";

class InteractionPlugin extends LivingMapPlugin {
  private shouldSelectLocationUI: boolean | null = null;

  private selectedFeatureSource: mapboxgl.GeoJSONSource | null = null;

  private userClickActioner: UserClickAction;
  private mouseMoveActioner: UserMouseMoveAction;

  private geometrySelector: MapGeometrySelector;
  private geometryHighlighter: MapGeometryHighlighter;
  private geometryHoverer: MapGeometryHover;

  private handleFeatureSelect: ((feature: LMFeature) => void) | null;
  private routingControlInstance: RoutingPlugin;

  constructor(
    id: string,
    LMMap: LivingMap,
    { debug = false },
    routingControlInstance: RoutingPlugin,
  ) {
    super(id, LMMap);

    this.userClickActioner = new UserClickAction(LMMap);
    this.mouseMoveActioner = new UserMouseMoveAction(LMMap, { debug });

    this.geometrySelector = new MapGeometrySelector(LMMap);
    this.geometryHighlighter = new MapGeometryHighlighter(LMMap);
    this.geometryHoverer = new MapGeometryHover(LMMap);
    this.handleFeatureSelect = null;
    this.routingControlInstance = routingControlInstance;
  }

  /**
   * public activate enables functionality of map control
   * @public
   */
  activate(): void {
    const mapInstance = this.LMMap.getMapboxMap();
    mapInstance.on("click", this.userClickActioner.handle);
    mapInstance.on("mousemove", this.mouseMoveActioner.handle);

    if (mapInstance.getSource(SourceIds.SELECTED_FEATURE_SOURCE_ID) !== null) {
      this.selectedFeatureSource = mapInstance.getSource(
        SourceIds.SELECTED_FEATURE_SOURCE_ID,
      ) as mapboxgl.GeoJSONSource;
    }

    const filterInstance = this.LMMap.getFilterKing();
    filterInstance.updateLocalFilter(FeatureLayers.SELECTED_FEATURE_LAYER, {
      globalExclusions: [GlobalFilters.LAYERS],
    });
  }

  /**
   * public deactivate disables functionality of map control
   * @public
   */
  deactivate(): void {
    const mapInstance = this.LMMap.getMapboxMap();
    mapInstance.off("click", this.userClickActioner.handle);
    mapInstance.off("mousemove", this.mouseMoveActioner.handle);
  }

  public subscribeToFeatureSelect(callback: (feature: LMFeature) => void) {
    this.handleFeatureSelect = callback;
  }

  public selectFeature(
    feature: LMFeature,
    options?: SelectFeatureOptions,
  ): void {
    const opts: SelectFeatureOptions = {
      silent: false,
      force: false,
      useFeatureSelectHandler: true,
      ...options,
    };

    // when selecting a geometry, we want to ensure no geometry has an active visualisatoin state.
    this.geometryHighlighter.unhighlight(false);
    this.geometryHoverer.unhover(false);

    this.geometrySelector.select(feature, opts);
    this.LMMap.easeTo(feature);

    if (opts.useFeatureSelectHandler)
      this.handleFeatureSelect && this.handleFeatureSelect(feature);

    this.routingControlInstance.rerenderRoutePins();
  }

  public deselectFeatures(silent = false): void {
    this.geometrySelector.unselect(silent);

    if (!silent) {
      this.LMMap.emit("LiveLocationSelected", null);
    }
  }

  public highlightFeature(feature: LMFeature): boolean {
    return this.geometryHighlighter.highlight(feature);
  }

  public dehighlightFeatures(): boolean {
    return this.geometryHighlighter.unhighlight();
  }

  public clearSelectedFeatureSource(): void {
    if (!this.selectedFeatureSource) return;

    this.selectedFeatureSource.setData({
      type: "FeatureCollection",
      features: [],
    });
  }

  public updateSelectedFeatureSource(feature: LMFeature): void {
    if (!this.selectedFeatureSource) return;

    // Note: we need to mutate the feature properties for some reason
    // unknown as to why.
    // TODO: perhaps add a LMFeature.copy() And an LMFeature.Builder.withGeometry().withProperties().build() ???
    const mapboxFeature = Object.assign({}, feature.getMapboxFeature());

    const centroid = feature.getCentroid();
    if (!centroid)
      throw new Error(
        `Centroid does not exist on LMFeature for: ${feature.getId()}`,
      );

    mapboxFeature.geometry = {
      type: "Point",
      coordinates: centroid,
    };

    mapboxFeature.properties = {
      ...mapboxFeature.properties,
      selected: "active",
      preventClickPropagation: true,
      pin_type: feature.getPinType(),
    };

    delete mapboxFeature.properties.floor_id;
    delete mapboxFeature.properties.floor_name;

    this.selectedFeatureSource!.setData({
      type: "FeatureCollection",
      features: [mapboxFeature],
    });
  }

  public getSelectLocationUI(): boolean | null {
    return this.shouldSelectLocationUI;
  }
}

export default InteractionPlugin;
